/* @flow */
import idx from 'idx';
import { isEmpty } from 'lodash';
import * as React from 'react';
import Modal from 'react-modal';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';

import LoadingIndicator from 'components/LoadingIndicator.jsx';
import ErrorReporter from 'app/components/ErrorReporter.jsx';
import BoxBody from 'components/BoxBody.jsx';
import Dialog from 'components/Dialog.jsx';
import Button from 'components/Button.jsx';
import Heading from 'components/Heading.jsx';
import Text from 'components/Text.jsx';
import List from 'components/List.jsx';
import ListItem from 'components/ListItem.jsx';
import Row from 'components/Row.jsx';
import Box from 'components/Box.jsx';
import Divider from 'components/Divider.jsx';
import Notice from 'components/Notice.jsx';

import IndividualPlanLink from './IndividualPlanLink.jsx';

import './styles/IndividualPlanView.scss';

type Props = {
  data: ReactApolloData<IndividualPlanViewQuery>,
};

type State = {
  showMobilePlanModal: boolean,
};

class IndividualPlanView extends React.Component<Props, State> {
  state: State = {
    showMobilePlanModal: false,
  };

  renderFreePlanSections = (freeIndividualPlan: Object) => {
    const freePlan = !isEmpty(freeIndividualPlan);

    const freePlanSection = (
      <Box>
        {freePlan ? this.renderFreePlanHeader(freeIndividualPlan.activeSince) : null}
        <Divider size="small" />
        {freePlan ? this.renderFreePlanFeatures(freeIndividualPlan.productDetails) : null}
      </Box>
    );

    return freePlanSection;
  };

  renderFreePlanHeader = (freePlanActiveDate: string) => {
    return (
      <div className="IndividualPlanView-box-descriptive-heading">
        <div>
          <div className="IndividualPlanView-box-freePlanText">
            <Text size="medium" weight="semibold">
              {' Free Plan '}
            </Text>
          </div>
          <div>
            <Text color="eclipse" size="xsmall">
              {freePlanActiveDate}
            </Text>
          </div>
        </div>
        <div className="IndividualPlanView-box-descriptive-heading-element">
          <Text color="eclipse" size="medium">
            You are currently on this plan
          </Text>
        </div>
      </div>
    );
  };

  renderFreePlanFeatures = (freePlanSpecs: Array<*>) => {
    const listItems = freePlanSpecs.map(item => (
      <ListItem>
        <Text size="small"> {item.text}</Text>
      </ListItem>
    ));

    return (
      <div>
        <div className="IndividualPlanView-box-heading">
          <Text color="eclipse" size="medium" weight="semibold">
            Features
          </Text>
        </div>
        <div className="IndividualPlanView-box-list">
          <List marker="bullet">{listItems}</List>
        </div>
      </div>
    );
  };

  renderPaidPlanSections = (purchasedIndividualPlan: Object, purchasableIndividualPlan: Object) => {
    const freePlan = isEmpty(purchasedIndividualPlan);
    const purchasablePlan = !isEmpty(purchasableIndividualPlan);

    const purchasePlanSection = (
      <Box size="xlarge">
        {purchasablePlan
          ? this.renderPurchasePlanHeader(
              freePlan,
              purchasableIndividualPlan.priceDescription,
              !freePlan ? purchasedIndividualPlan.purchasedAt : ''
            )
          : null}
        <Divider size="small" />
        {purchasablePlan
          ? this.renderPaidPlanFeatures(purchasableIndividualPlan.productDetails)
          : null}
        {this.renderGetAccess(freePlan)}
      </Box>
    );

    return purchasePlanSection;
  };

  renderPurchasePlanHeader = (
    freePlan: boolean,
    purchasePlanPrice: string,
    purchasePlanDate: string
  ) => {
    let purchasePlanDetails = (
      <div>
        <div className="IndividualPlanView-box-freePlanText">
          <Text size="medium" weight="semibold">
            {' Individual Plan '}
          </Text>
        </div>
        <div>
          <Text color="eclipse" size="xsmall">
            Active since {purchasePlanDate}
          </Text>
        </div>
      </div>
    );

    let purchaseMemberDetails = (
      <Text color="eclipse" size="medium">
        You are currently on this plan
      </Text>
    );

    if (freePlan) {
      purchasePlanDetails = (
        <div className="IndividualPlanView-box-descriptive-heading-element">
          <div className="IndividualPlanView-box-headingGroup">
            <div className="IndividualPlanView-box-headingGroup-text">
              <Text size="large" weight="semibold">
                Purchase an individual Plan
              </Text>
            </div>
          </div>
          <div className="IndividualPlanView-box-headingGroup">
            <div className="IndividualPlanView-box-headingGroup-notice">
              <Notice circleRadius color="shamrock">
                Recommended
              </Notice>
            </div>
          </div>
        </div>
      );

      purchaseMemberDetails = (
        <Text size="large" weight="semibold">
          {purchasePlanPrice}
        </Text>
      );
    }

    return (
      <div className="IndividualPlanView-box-descriptive-heading">
        {purchasePlanDetails}
        <div className="IndividualPlanView-box-descriptive-heading-element">
          {purchaseMemberDetails}
        </div>
      </div>
    );
  };

  renderPaidPlanFeatures = (paidPlanFeatures: Array<*>) => {
    const paidFeatures = paidPlanFeatures.map(item => (
      <div className="IndividualPlanView-box-list-item">
        <ListItem>
          <Text size="small">{item.text}</Text>
        </ListItem>
      </div>
    ));

    return (
      <div>
        <div className="IndividualPlanView-box-heading">
          <Text color="eclipse" size="medium" weight="semibold">
            Features
          </Text>
        </div>
        <div className="IndividualPlanView-box-text">
          <Text size="medium">All of the features from the Free Plan plus:</Text>
        </div>
        <div className="IndividualPlanView-box-list">
          <List marker="bullet">{paidFeatures}</List>
        </div>
      </div>
    );
  };

  renderMobilePlanModal = () => {
    this.setState({ showMobilePlanModal: true });
  };

  renderGetAccess = (freePlan: boolean) => {
    let buttons = (
      <div className="IndividualPlanView-box-buttonGroup">
        <div className="IndividualPlanView-box-button">
          <Button onClick={this.renderMobilePlanModal} color="lava" level="primary">
            Cancel subscription
          </Button>
        </div>
      </div>
    );

    if (freePlan) {
      buttons = (
        <div className="IndividualPlanView-box-buttonGroup">
          <div className="IndividualPlanView-box-button">
            <Button onClick={this.renderMobilePlanModal} level="secondary">
              Tell your administrator
            </Button>
          </div>
          <div className="IndividualPlanView-box-button">
            <Button onClick={this.renderMobilePlanModal} level="primary">
              Upgrade Now
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div>
        <Modal isOpen={this.state.showMobilePlanModal}>
          <Dialog
            onRequestHide={() => this.setState({ showMobilePlanModal: false })}
            eventScreenName="TextAppLinkDialog"
            className="TextAppLinkDialog"
            size="small">
            <BoxBody>
              <IndividualPlanLink />
            </BoxBody>
          </Dialog>
        </Modal>
        <div className="IndividualPlanView-box-heading">
          <Text color="eclipse" size="medium" weight="semibold">
            Get Access
          </Text>
        </div>
        <div className="IndividualPlanView-box-text">
          <Text size="medium">
            You can get access to these features by clicking Upgrade now to purchase an
            <Text size="medium" weight="semibold">
              {' Individual Plan '}
            </Text>
            , or reach out to your administrator about purchasing a
            <Text size="medium" weight="semibold">
              {' School/District Plan '}
            </Text>
          </Text>
          {buttons}
        </div>
      </div>
    );
  };

  render() {
    const { data } = this.props;

    if (data.loading) {
      return <LoadingIndicator />;
    }

    if (data.error) {
      return <ErrorReporter error={data.error} />;
    }

    const individualPlans = idx(data.me, _ => _.individualPlans);

    const freeIndividualPlan = individualPlans
      ? individualPlans.find(plan => plan.__typename === 'FreeIndividualPlan')
      : null;
    const purchasedIndividualPlan = individualPlans
      ? individualPlans.find(plan => plan.__typename === 'PurchasedIndividualPlan')
      : null;
    const purchasableIndividualPlan = individualPlans
      ? individualPlans.find(plan => plan.__typename === 'PurchasableIndividualPlan')
      : null;

    return (
      <div className="IndividualPlanView">
        <Row>
          <Heading level="2"> Remind Plans </Heading>
          <Text color="eclipse" size="medium">
            Use Remind in a way that fits your personal needs.
          </Text>
        </Row>
        <Row>{this.renderFreePlanSections(freeIndividualPlan || {})}</Row>
        <Row>
          {this.renderPaidPlanSections(
            purchasedIndividualPlan || {},
            purchasableIndividualPlan || {}
          )}
        </Row>
      </div>
    );
  }
}

const withData = graphql(
  gql`
    query IndividualPlanView {
      me {
        id
        uuid
        individualPlans(includeFreePlan: true) {
          productTitle
          productDetails {
            text
          }

          ... on FreeIndividualPlan {
            activeSince
          }

          ... on PurchasedIndividualPlan {
            purchasedAt
            activeUntil
          }

          ... on PurchasableIndividualPlan {
            purchaseOptions {
              monthly {
                priceText
              }
              yearly {
                priceText
              }
            }
          }
        }
      }
    }
  `
);

export { IndividualPlanView };
export default withData(IndividualPlanView);
